import React from 'react'
import { AxiosError } from 'axios'
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import { Button, Space, Spin, Typography } from 'antd'

export type TRefundStep = 'confirm' | 'loading' | 'success' | 'error'

// Текст ошибки от ЮKassa приходит в ответе бэкенда: response.data.message
export const getServerErrorText = (e: unknown): string => {
  const error = e as AxiosError<{ message?: string }>
  return (
    error?.response?.data?.message ??
    'Не удалось выполнить возврат. Попробуйте позже.'
  )
}

// Отмена заказа и удаление товара ведут себя одинаково: ждём ответ бэкенда о возврате
// денег покупателю и по нему показываем результат
export const useRefundFlow = (isOpen: boolean) => {
  const [step, setStep] = React.useState<TRefundStep>('confirm')
  const [errorText, setErrorText] = React.useState('')

  React.useEffect(() => {
    if (isOpen) {
      setStep('confirm')
      setErrorText('')
    }
  }, [isOpen])

  const runRefund = async (refund: () => Promise<void>) => {
    setStep('loading')
    try {
      await refund()
      setStep('success')
    } catch (e) {
      setErrorText(getServerErrorText(e))
      setStep('error')
    }
  }

  const backToConfirm = () => setStep('confirm')

  return { step, errorText, runRefund, backToConfirm }
}

interface IRefundFlowStatesProps {
  step: TRefundStep
  errorText: string
  loadingText: string
  successText: string
  errorTitle?: string
  onClose: () => void
  onRetry: () => void
}

export const RefundFlowStates: React.FC<IRefundFlowStatesProps> = ({
  step,
  errorText,
  loadingText,
  successText,
  errorTitle = 'Не удалось провести возврат',
  onClose,
  onRetry
}) => (
  <>
    {step === 'loading' && (
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        <Spin size='large' />
        <p style={{ marginTop: 16 }}>{loadingText}</p>
      </div>
    )}

    {step === 'success' && (
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <CheckCircleFilled style={{ fontSize: 48, color: '#52c41a' }} />
        <p style={{ marginTop: 16 }}>{successText}</p>
        <Button type='primary' onClick={onClose} style={{ marginTop: 8 }}>
          Закрыть
        </Button>
      </div>
    )}

    {step === 'error' && (
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <CloseCircleFilled style={{ fontSize: 48, color: '#ff4d4f' }} />
        <p style={{ marginTop: 16 }}>{errorTitle}</p>
        <Typography.Text type='danger'>{errorText}</Typography.Text>
        <div>
          <Space style={{ marginTop: 16 }}>
            <Button onClick={onClose}>Закрыть</Button>
            <Button type='primary' onClick={onRetry}>
              Повторить
            </Button>
          </Space>
        </div>
      </div>
    )}
  </>
)
